import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import './phone.css'

const LEAVE_MS = 320
/** Dragging the grip down this far closes the sheet. */
const DRAG_CLOSE = 80

interface Props {
  open: boolean
  onClose: () => void
  /** The sheet's name for screen readers (and its heading when `title` is left out). */
  label: string
  /** A visible heading, with an optional count or hint beside it. */
  title?: ReactNode
  aside?: ReactNode
  children: ReactNode
}

/**
 * The phone's bottom sheet (handoff-19 Mobile Chat Prototype): slides up over a
 * dimmed screen, closes on the backdrop, Escape or dragging the grip down.
 */
export function PhoneSheet({ open, onClose, label, title, aside, children }: Props) {
  const { t } = useTranslation('chats')
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)
  const [drag, setDrag] = useState(0)
  const start = useRef<{ id: number; y: number } | null>(null)
  /** A drag of the grip is not also a tap on it. */
  const moved = useRef(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)))
      return () => cancelAnimationFrame(id)
    }
    setShown(false)
    const id = window.setTimeout(() => setMounted(false), LEAVE_MS)
    return () => window.clearTimeout(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!mounted) return null
  return createPortal(
    <div className={`ph-sheet-layer ${shown ? 'is-open' : ''}`}>
      <div className="ph-sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className={`ph-sheet ${drag ? 'is-dragging' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
      >
        <button
          type="button"
          className="ph-sheet-grip"
          aria-label={t('phone.closeSheet')}
          onClick={() => !moved.current && onClose()}
          onPointerDown={(e) => {
            moved.current = false
            start.current = { id: e.pointerId, y: e.clientY }
            e.currentTarget.setPointerCapture?.(e.pointerId)
          }}
          onPointerMove={(e) => {
            if (start.current?.id !== e.pointerId) return
            const dy = Math.max(0, e.clientY - start.current.y)
            if (dy > 4) moved.current = true
            setDrag(dy)
          }}
          onPointerUp={() => {
            const far = drag >= DRAG_CLOSE
            start.current = null
            setDrag(0)
            if (far) onClose()
          }}
          onPointerCancel={() => {
            start.current = null
            setDrag(0)
          }}
        >
          <span aria-hidden="true" />
        </button>
        <div className="ph-sheet-body">
          {title != null && (
            <div className="ph-sheet-head">
              <span className="ph-sheet-title">{title}</span>
              {aside != null && <span className="ph-sheet-aside">{aside}</span>}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
